/* eslint-disable @next/next/no-img-element */
"use client";

import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import ReactCrop, {
  Crop,
  PixelCrop,
  centerCrop,
  makeAspectCrop,
} from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { getCroppedImg } from "@/utils/cropUtils";

interface ImageCropperModalProps {
  labels?: { title: string; close: string; alt: string; busy: string; confirm: string };
  isOpen: boolean;
  onClose: () => void;
  imageSrc: string;
  onCropComplete: (croppedBlob: Blob) => void | Promise<void>;
}

const ImageCropperModal = ({
  labels,
  isOpen,
  onClose,
  imageSrc,
  onCropComplete,
}: ImageCropperModalProps) => {
  const imgRef = useRef<HTMLImageElement>(null);
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [isLoading, setIsLoading] = useState(false);
  const [mounted, setMounted] = useState(false);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const isLoadingRef = useRef(isLoading);
  const onCloseRef = useRef(onClose);

  useEffect(() => setMounted(true), []);

  useEffect(() => {
    isLoadingRef.current = isLoading;
    onCloseRef.current = onClose;
  }, [isLoading, onClose]);

  useEffect(() => {
    setCrop(undefined);
    setCompletedCrop(undefined);
  }, [imageSrc]);

  useEffect(() => {
    if (!isOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !isLoadingRef.current) onCloseRef.current();
    };
    window.addEventListener("keydown", handleKeyDown);
    const focusFrame = window.requestAnimationFrame(() =>
      closeButtonRef.current?.focus(),
    );

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
      window.cancelAnimationFrame(focusFrame);
    };
  }, [isOpen]);

  function onImageLoad(e: React.SyntheticEvent<HTMLImageElement>) {
    const { width, height } = e.currentTarget;
    const initialCrop = centerCrop(
      makeAspectCrop({ unit: "%", width: 90 }, 1, width, height),
      width,
      height,
    );
    setCrop(initialCrop);
  }

  async function handleConfirmCrop() {
    if (!completedCrop || !imgRef.current) {
      return;
    }
    setIsLoading(true);
    try {
      const croppedBlob = await getCroppedImg(
        imgRef.current,
        completedCrop,
        "profile.jpg",
      );
      await onCropComplete(croppedBlob);
      onClose();
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  }

  if (!mounted || !isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-2 sm:p-6">
      <button
        type="button"
        aria-label={labels?.close ?? "Close image cropper"}
        className="absolute inset-0 h-full w-full bg-black/75"
        onClick={() => {
          if (!isLoading) onClose();
        }}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="image-cropper-title"
        className="relative flex max-h-[calc(100dvh-1rem)] w-full max-w-lg flex-col overflow-hidden rounded-xl bg-white shadow-2xl sm:max-h-[calc(100dvh-3rem)]"
      >
        <div className="flex shrink-0 items-center justify-between border-b border-gray-200 px-4 py-3">
          <h2 id="image-cropper-title" className="font-semibold text-slate-900">
            {labels?.title ?? "Crop profile image"}
          </h2>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            disabled={isLoading}
            aria-label={labels?.close ?? "Close"}
            className="bg-grayCustom text-slateCustom hover:text-pinkCustom flex h-11 w-11 items-center justify-center rounded-full transition focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-700 disabled:opacity-50"
          >
            <X className="h-4 w-4" strokeWidth={4} />
          </button>
        </div>
        <div className="flex min-h-0 flex-1 items-center justify-center overflow-auto bg-slate-950 p-2 sm:p-4">
          <ReactCrop
            crop={crop}
            onChange={(_, percentCrop) => setCrop(percentCrop)}
            onComplete={(c) => setCompletedCrop(c)}
            aspect={1}
            minWidth={100}
            className="max-w-full"
          >
            <img
              ref={imgRef}
              alt={labels?.alt ?? "Crop me"}
              src={imageSrc}
              onLoad={onImageLoad}
              className="max-h-[calc(100dvh-10rem)] max-w-full object-contain sm:max-h-[calc(100dvh-13rem)]"
            />
          </ReactCrop>
        </div>
        <div className="flex shrink-0 justify-end border-t border-gray-200 bg-white px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
          <button
            type="button"
            onClick={handleConfirmCrop}
            disabled={isLoading || !completedCrop}
            className="bg-slateCustom min-h-11 rounded-lg px-5 py-2 font-medium text-white transition hover:opacity-90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-700 disabled:cursor-not-allowed disabled:bg-gray-400"
          >
            {isLoading ? labels?.busy ?? "Cropping..." : labels?.confirm ?? "Confirm Crop"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ImageCropperModal;
